import { TableRow, TableCell, IconButton, Collapse, Box, Table, TableHead, TableBody, Tooltip, Typography, Grid } from "@material-ui/core";
import React from "react";
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import ImportExportIcon from '@material-ui/icons/ImportExport';
import LinesContext from "./LinesContext";
import { LineSystemCollection, SystemButtonStateCollection } from './SystemsContext';
import RemoveIcon from '@material-ui/icons/Remove';
import { Utilities } from "../general/utilities";
import { IdSet, LinesView, SystemCallbackType } from "./Paperbase";
import { DeltaXMLColors, MyDeltaColors } from "./styling/DeltaColors";
import { LicenseSort } from "../general/sorting/LicenseSort";
import { SortOrder, SortBy, SortView, SortViewData, SortData } from "../general/sorting/LicenseSortParams";
import { DTFormatter } from "../general/datetime-formatter/DTFormatter";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      '& > *': {
        borderBottom: 'unset',
      },
    },
    emptyDealRow: {
      color: MyDeltaColors.FADE_OXFORD,
      backgroundColor: MyDeltaColors.FADE_GREY
    },
    emptyRenwealDealRow: {
      backgroundColor: MyDeltaColors.FADE_GREY
    },
    linesRow: {
        background: DeltaXMLColors.CELERY,
    },
    linesRowCellText: {
        color: MyDeltaColors.WHITE,
        whiteSpace: 'nowrap',
        overflow: 'hidden'
    },
    renewalPassedTableCellText: {
      color: DeltaXMLColors.VALENTINE_RED,
    },
    withinRenewalTableCellText: {
      color: DeltaXMLColors.OXFORD_BLUE
    },
    cellWithTooltip: {
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer'
    },
    sortButton: {
      color: MyDeltaColors.WHITE,
      backgroundColor: 'transparent',
      padding: '3px',
      marginRight: '5px'
    }
  });

export interface LinesData {
  "licenseExpiry": string,
  "licenseModel": string,
  "licenseType": string,
  "lineNumber": number,
  "product": string,
  "quantity": number,
  "currentQuantity": number,
  "supportExpiry": string,
  "allowedHostIds": string[],
  "lineViewExpanded"?: boolean,
  "isCurrent": boolean,
  "inputSizeLimitMb": number,
  "capabilities": CapabilitiesData[]
}
export interface CapabilitiesData {
  "description": string,
  "shortName": string,
  "name": string
}

export type DealLineCollection = { [name: string]: InnerLineCollection };

export interface ViewDealLineCollection {
  [LinesView.CURRENT] : DealLineCollection;
  [LinesView.CURRENT_SUBS] : DealLineCollection;
  [LinesView.CURRENT_EVALS] : DealLineCollection;
  [LinesView.HISTORIC] : DealLineCollection;
  [LinesView.HISTORIC_SUBS] : DealLineCollection;
  [LinesView.HISTORIC_EVALS] : DealLineCollection;
}

export function InitViewDealCollection(): ViewDealLineCollection {
  return {
    [LinesView.CURRENT]: {},
    [LinesView.CURRENT_SUBS]: {},
    [LinesView.CURRENT_EVALS]: {},
    [LinesView.HISTORIC]: {},
    [LinesView.HISTORIC_SUBS]: {},
    [LinesView.HISTORIC_EVALS]: {},
  }
}
export type InnerLineCollection = { [name: string]: LinesData }

export interface RowProps {
  "accountName": string,
  "dealId": string,
  "licenseType": string,
  "lineCount": number,
  "supportRenewalDate": string
}

export interface DealsContextProps extends WithStyles<typeof styles> {
  dealId: string,
  row: RowProps,
  lines: InnerLineCollection,
  dealOpen: boolean,
  linesSystemData: LineSystemCollection,
  systemButtons: SystemButtonStateCollection;
  callSystemAction: SystemCallbackType;
  createPartialFulfillmentSystem: (idSet: IdSet, fulfillmentSize: number) => void;
  handleDealOpen: (id: string, view: LinesView) => void,
  handleLineOpen: (dealId: string, lineId: string, view: LinesView) => void;
  downloadLicense: (dealId: string, lineNumber: string, systemId: string, licenseModel: string) => void;
  addUserAnnotationToSystem: (idSet: IdSet, user_annotation: string) => void;
  handleSortViewChange: (sortView: SortView, sortData: SortData) => void;
  sortViewData: SortViewData;
  linesView: LinesView,
  enableSystemActionButtons: boolean;
  dateFormat: string;
  timeFormat: string;
  capabilities: CapabilitiesData[]
}

function DealsContext(props: DealsContextProps) {
  const { lines, linesView, row, classes, sortViewData, handleSortViewChange, handleDealOpen, handleLineOpen, dealOpen, dealId, linesSystemData, systemButtons, callSystemAction, createPartialFulfillmentSystem, downloadLicense, addUserAnnotationToSystem, enableSystemActionButtons, dateFormat, timeFormat, capabilities } = props;
  const linesExist = lines && Object.entries(lines).length > 0;
  const renewalDatePassed = Utilities.hasExpired(row.supportRenewalDate);
  const sortData = sortViewData[SortView.LINES];

  const lineContextComponents = linesExist ? Object.entries(lines)
    .sort((a: any, b: any) => LicenseSort.sortDealsTable(a, b, sortData.order, sortData.sortBy))
    .map((kvp: [string, LinesData]) => {

      const [name, value] = kvp;
      const dealLineId = dealId + ' ' + value.lineNumber;
      const lineOpen = !!value.lineViewExpanded;

      return <LinesContext key={name} linesView={linesView} sortViewData={sortViewData} handleSortViewChange={handleSortViewChange}
                           enableSystemActionButtons={enableSystemActionButtons} dealId={dealId} lineNumber={name} line={value}
                           systemCollection={linesSystemData[dealLineId]} handleLineOpen={handleLineOpen} lineOpen={lineOpen} systemButtons={systemButtons}
                           callSystemAction={callSystemAction} createPartialFulfillmentSystem={createPartialFulfillmentSystem} downloadLicense={downloadLicense}
                           addUserAnnotationToSystem={addUserAnnotationToSystem} dateFormat={dateFormat} timeFormat={timeFormat} capabilities={capabilities} />
    }) : [];

    const onSortChange = (sortBy: SortBy) => {
      let nextSort = sortData.order === SortOrder.DESCENDING ?
        SortOrder.ASCENDING :
        SortOrder.DESCENDING;
  
      handleSortViewChange(SortView.LINES, { order: nextSort, sortBy: sortBy} )
    }

  const dealClassName = linesExist? '' : classes.emptyDealRow + ' ';
  const dealDealClassName = linesExist? '' : classes.emptyRenwealDealRow + ' ';
  const renewCellClassName = renewalDatePassed? dealDealClassName + classes.renewalPassedTableCellText : dealDealClassName + classes.withinRenewalTableCellText; 

  return (
    <React.Fragment>
      <TableRow className={classes.root}>
        <TableCell className={dealClassName}>
          <IconButton style={{ padding: '3px 0px 3px 0px' }} aria-label="expand row" size="small" onClick={() => handleDealOpen(dealId, linesView)}>
            {!linesExist ? <RemoveIcon /> : dealOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell className={dealClassName} component="th" scope="row">
          {row.accountName}
        </TableCell>
        <TableCell className={dealClassName}>{row.dealId}</TableCell>
        <TableCell className={dealClassName}>{row.licenseType}</TableCell>
        <TableCell className={renewCellClassName && (renewalDatePassed) ? classes.renewalPassedTableCellText : classes.withinRenewalTableCellText}>{row.supportRenewalDate.length > 0 ? DTFormatter.getFormattedDate(row.supportRenewalDate, dateFormat) : row.supportRenewalDate}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={dealOpen && linesExist} timeout="auto" unmountOnExit>
            <Box margin={0}>
              <Table size="small" aria-label="purchases" >
                <colgroup>
                  <col width="0%" />
                  <col width="5%" />
                  <col width="20%" />
                  <col width="15%" />
                  <col width="20%" />
                  <col width="15%" />
                  <col width="15%" />
                  <col width="10%" />
                </colgroup>
                <TableHead>
                  <TableRow className={classes.linesRow}>
                    <TableCell />
                    <TableCell className={classes.linesRowCellText}>#</TableCell>
                    <TableCell className={classes.linesRowCellText}>
                      <Tooltip title="The name of the licensed solution" placement="bottom-start">
                        <Grid container direction="row" onClick={() => onSortChange(SortBy.PRODUCT)}>
                          <Grid item>
                            <IconButton className={classes.sortButton} size="small">
                              <ImportExportIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                          <Grid item>
                            <Typography className={classes.cellWithTooltip}>Solution</Typography>
                          </Grid>
                        </Grid>
                      </Tooltip>
                    </TableCell>
                    <TableCell className={classes.linesRowCellText}>
                      <Tooltip title="The agreed type of usage e.g. Production, Non-production, Evaluation" placement="bottom-start">
                        <Grid container direction="row" onClick={() => onSortChange(SortBy.LICENSE_TYPE)}>
                          <Grid item>
                            <IconButton className={classes.sortButton} size="small">
                              <ImportExportIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                          <Grid item>
                            <Typography className={classes.cellWithTooltip}>Usage</Typography>
                          </Grid>
                        </Grid>
                      </Tooltip>
                    </TableCell>
                    <TableCell className={classes.linesRowCellText}>
                      <Tooltip title="The License Model. Please see licensing documentation for description" placement="bottom-start">
                        <Grid container direction="row" onClick={() => onSortChange(SortBy.LICENSE_MODEL)}>
                          <Grid item>
                            <IconButton className={classes.sortButton} size="small">
                              <ImportExportIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                          <Grid item>
                            <Typography className={classes.cellWithTooltip}>License Type</Typography>
                          </Grid>
                        </Grid>
                      </Tooltip>
                    </TableCell>
                    <TableCell className={classes.linesRowCellText}>
                      <Tooltip title="The date on which the license will no longer run" placement="bottom-start">
                        <Grid container direction="row" onClick={() => onSortChange(SortBy.LICENSE_EXPIRY)}>
                          <Grid item>
                            <IconButton className={classes.sortButton} size="small">
                              <ImportExportIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                          <Grid item>
                            <Typography className={classes.cellWithTooltip}>License Expiry</Typography>
                          </Grid>
                        </Grid>
                      </Tooltip>
                    </TableCell>
                    <TableCell className={classes.linesRowCellText}>
                      <Tooltip title="The total number of licenses available" placement="bottom-start">
                        <Grid container direction="row" onClick={() => onSortChange(SortBy.QUANTITY)}>
                          <Grid item>
                            <IconButton className={classes.sortButton} size="small">
                              <ImportExportIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                          <Grid item>
                            <Typography className={classes.cellWithTooltip}>Pipe Size/Quantity</Typography>
                          </Grid>
                        </Grid>
                      </Tooltip>
                    </TableCell>
                    <TableCell className={classes.linesRowCellText}/>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineContextComponents}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default withStyles(styles)(DealsContext);